import { Skeleton, Tooltip, UnstyledButton, type UnstyledButtonProps } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import {
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconLock,
  IconSelector,
} from "@tabler/icons-react";
import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from "react";
import classes from "./na.module.css";

export const naClasses = classes;

const cx = (...c: (string | false | null | undefined)[]) =>
  c.filter(Boolean).join(" ");

/* ------------------------------------------------------------------ */
/* CornerButton                                                        */
/* ------------------------------------------------------------------ */

type CornerButtonOwnProps<C extends ElementType> = {
  component?: C;
  leftSection?: ReactNode;
  size?: "sm" | "md";
  danger?: boolean;
  /** Primary fill without the corner brackets (secondary primaries in a card). */
  plain?: boolean;
} & UnstyledButtonProps;

export type CornerButtonProps<C extends ElementType = "button"> =
  CornerButtonOwnProps<C> &
    Omit<ComponentPropsWithoutRef<C>, keyof CornerButtonOwnProps<C>>;

/** Primary action per surface: square fill with four corner brackets. */
export function CornerButton<C extends ElementType = "button">({
  component,
  leftSection,
  size = "md",
  danger,
  plain,
  className,
  children,
  ...rest
}: CornerButtonProps<C>) {
  return (
    <UnstyledButton
      component={(component ?? "button") as "button"}
      className={cx(classes.corner, className)}
      data-size={size}
      data-danger={danger || undefined}
      {...(rest as object)}
    >
      {!plain && (
        <>
          <span className={classes.bracket} data-c="tl" />
          <span className={classes.bracket} data-c="tr" />
          <span className={classes.bracket} data-c="bl" />
          <span className={classes.bracket} data-c="br" />
        </>
      )}
      {leftSection}
      {children}
    </UnstyledButton>
  );
}

/* ------------------------------------------------------------------ */
/* Status                                                              */
/* ------------------------------------------------------------------ */

export type StatusTone =
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "muted"
  | "sky";

export const toneColor: Record<StatusTone, string> = {
  success: "var(--na-success)",
  warning: "var(--na-warning)",
  danger: "var(--na-danger)",
  info: "var(--na-info)",
  muted: "var(--na-fg-muted)",
  sky: "var(--na-sky-4)",
};

export function StatusDot({
  tone,
  pulse,
  style,
}: {
  tone: StatusTone;
  pulse?: "slow" | "fast";
  style?: CSSProperties;
}) {
  return (
    <span
      aria-hidden
      className={classes.dot}
      data-pulse={pulse}
      style={{ color: toneColor[tone], ...style }}
    />
  );
}

/** Dot plus label in the status colour. `pill` gives the outlined header form. */
export function StatusBadge({
  tone,
  label,
  pulse,
  pill,
}: {
  tone: StatusTone;
  label: ReactNode;
  pulse?: "slow" | "fast";
  pill?: boolean;
}) {
  return (
    <span
      className={classes.status}
      data-pill={pill || undefined}
      style={{ color: tone === "danger" ? "var(--na-danger-text)" : toneColor[tone] }}
    >
      <StatusDot tone={tone} pulse={pulse} />
      {label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Tag                                                                 */
/* ------------------------------------------------------------------ */

export type TagTone = "default" | "info" | "sky" | "success" | "warning" | "danger" | "count";

export function Tag({
  tone = "default",
  children,
  title,
  style,
}: {
  tone?: TagTone;
  children: ReactNode;
  title?: string;
  style?: CSSProperties;
}) {
  return (
    <span className={classes.tag} data-tone={tone} title={title} style={style}>
      {children}
    </span>
  );
}

export const SoonTag = () => <Tag tone="info">Soon</Tag>;

/* ------------------------------------------------------------------ */
/* Surface / HairlineGrid / Section                                    */
/* ------------------------------------------------------------------ */

export function Surface({
  title,
  icon,
  actions,
  children,
  className,
  style,
  bodyStyle,
  component: C = "section",
}: {
  title?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  bodyStyle?: CSSProperties;
  component?: ElementType;
}) {
  return (
    <C className={cx(classes.surface, className)} style={style}>
      {(title || actions) && (
        <header className={classes.surfaceHead}>
          <h2 className={classes.surfaceTitle}>
            {icon}
            {title}
          </h2>
          {actions}
        </header>
      )}
      {bodyStyle ? <div style={bodyStyle}>{children}</div> : children}
    </C>
  );
}

export function HairlineGrid({
  min = 280,
  columns,
  children,
  style,
}: {
  min?: number;
  columns?: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      className={classes.hairline}
      style={{
        gridTemplateColumns: columns ?? `repeat(auto-fit, minmax(${min}px, 1fr))`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Section({
  title,
  icon,
  right,
  danger,
  children,
}: {
  title: ReactNode;
  icon?: ReactNode;
  right?: ReactNode;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={cx(classes.section, danger && classes.danger)}>
      <div className={classes.sectionTitle}>
        {icon}
        <span style={{ flex: 1 }}>{title}</span>
        {right}
      </div>
      {children}
    </section>
  );
}

/** Definition list in two columns: muted key, value. */
export function KeyValues({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className={classes.kv}>
      {items.map(([k, v], i) => (
        <div key={i} style={{ display: "contents" }}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ------------------------------------------------------------------ */
/* Segmented                                                           */
/* ------------------------------------------------------------------ */

export type SegmentedItem<T extends string> = {
  value: T;
  label: ReactNode;
  disabled?: boolean;
  title?: string;
};

/** Bordered button group with no gaps. `inverse` fills the active item. */
export function Segmented<T extends string>({
  value,
  onChange,
  data,
  inverse,
  size = "sm",
  label,
  style,
}: {
  value: T;
  onChange: (v: T) => void;
  data: SegmentedItem<T>[];
  inverse?: boolean;
  size?: "sm" | "md";
  label: string;
  style?: CSSProperties;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={classes.seg}
      data-inverse={inverse || undefined}
      data-size={size}
      style={style}
    >
      {data.map((d) => (
        <button
          key={d.value}
          type="button"
          role="radio"
          aria-checked={d.value === value}
          disabled={d.disabled}
          title={d.title}
          className={classes.segBtn}
          onClick={() => onChange(d.value)}
        >
          {d.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Page scaffolding                                                    */
/* ------------------------------------------------------------------ */

export function Page({
  title,
  eyebrow,
  subtitle,
  actions,
  maxWidth,
  children,
}: {
  title?: ReactNode;
  eyebrow?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  maxWidth?: number;
  children?: ReactNode;
}) {
  return (
    <div
      className={classes.page}
      style={maxWidth ? ({ "--na-page-max": `${maxWidth}px` } as CSSProperties) : undefined}
    >
      {title && (
        <div className={classes.pageHead} style={subtitle ? { alignItems: "flex-start" } : undefined}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {eyebrow && <div className={classes.eyebrow}>{eyebrow}</div>}
            <h1 className={classes.pageTitle}>{title}</h1>
            {subtitle && <div style={{ marginTop: 4 }}>{subtitle}</div>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* DataTable                                                           */
/* ------------------------------------------------------------------ */

/** A sortable head cell: the label plus sort state from `useSortable().header()`. */
export type SortableHeadCell = {
  label: ReactNode;
  sort: { dir: "asc" | "desc" | null; onSort: () => void };
};

const isSortableHeadCell = (h: unknown): h is SortableHeadCell =>
  typeof h === "object" && h !== null && "label" in h && "sort" in h;

/** Clickable column-header content with a direction caret. */
export function SortableHead({ label, sort }: SortableHeadCell) {
  const { dir, onSort } = sort;
  return (
    <button type="button" className={classes.sortHead} onClick={onSort}>
      <span>{label}</span>
      <span className={classes.sortCaret} data-on={dir != null || undefined} aria-hidden>
        {dir == null ? <IconSelector size={12} /> : dir === "asc" ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
      </span>
    </button>
  );
}

/** Div-grid table that scrolls horizontally inside its surface. */
export function DataTable({
  columns,
  minWidth,
  head,
  children,
  empty,
  isEmpty,
  loading,
}: {
  columns: string;
  minWidth?: number;
  head?: (ReactNode | SortableHeadCell)[];
  children?: ReactNode;
  empty?: ReactNode;
  isEmpty?: boolean;
  /** First load in flight: skeleton rows replace the body and the empty message. */
  loading?: boolean;
}) {
  const vars = {
    gridTemplateColumns: columns,
    "--na-table-min": minWidth ? `${minWidth}px` : undefined,
  } as CSSProperties;
  return (
    <div className={classes.tableScroll}>
      <div role="table" style={{ ["--na-cols" as string]: columns } as CSSProperties}>
        {head && (
          <div role="row" className={classes.headRow} style={vars}>
            {head.map((h, i) =>
              isSortableHeadCell(h) ? (
                <span
                  role="columnheader"
                  key={i}
                  aria-sort={h.sort.dir == null ? "none" : h.sort.dir === "asc" ? "ascending" : "descending"}
                >
                  <SortableHead label={h.label} sort={h.sort} />
                </span>
              ) : (
                <span role="columnheader" key={i}>
                  {h}
                </span>
              ),
            )}
          </div>
        )}
        {loading ? <SkeletonRows rows={4} height={16} /> : isEmpty ? <div className={classes.empty}>{empty}</div> : children}
      </div>
    </div>
  );
}

/** A table row; clickable rows are `role="link"` (never a button holding buttons). */
export function DataRow({
  columns,
  minWidth,
  onOpen,
  label,
  children,
  style,
}: {
  columns: string;
  minWidth?: number;
  onOpen?: () => void;
  label?: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      role={onOpen ? "link" : "row"}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={label}
      className={classes.row}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      style={
        {
          gridTemplateColumns: columns,
          "--na-table-min": minWidth ? `${minWidth}px` : undefined,
          ...style,
        } as CSSProperties
      }
    >
      {children}
    </div>
  );
}

export function CellMain({ title, sub }: { title: ReactNode; sub?: ReactNode }) {
  return (
    <div className={classes.cellMain}>
      <span className={classes.cellTitle}>{title}</span>
      {sub && <span className={classes.cellSub}>{sub}</span>}
    </div>
  );
}

/** Stops row navigation when clicking controls inside a clickable row. */
export const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

/* ------------------------------------------------------------------ */
/* Small bits                                                          */
/* ------------------------------------------------------------------ */

export function CopyText({
  value,
  display,
  label = "Copy",
}: {
  value: string;
  display?: ReactNode;
  label?: string;
}) {
  const { copy, copied } = useClipboard({ timeout: 1200 });
  return (
    <Tooltip label={copied ? "Copied" : label} withArrow={false}>
      <button
        type="button"
        className={classes.copy}
        aria-label={`${label} ${value}`}
        onClick={(e) => {
          e.stopPropagation();
          copy(value);
        }}
      >
        <span>{display ?? value}</span>
        {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
      </button>
    </Tooltip>
  );
}

export function ProgressBar({
  value,
  tone = "info",
  style,
}: {
  value: number;
  tone?: StatusTone;
  style?: CSSProperties;
}) {
  return (
    <div
      className={classes.bar}
      role="progressbar"
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
      style={style}
    >
      <span style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: toneColor[tone] }} />
    </div>
  );
}

export const Kbd = ({ children }: { children: ReactNode }) => (
  <kbd className={classes.kbd}>{children}</kbd>
);

/** Placeholder lines for content whose request is still in flight. */
export function SkeletonRows({
  rows = 3,
  height = 14,
  label = "Loading",
}: {
  rows?: number;
  height?: number;
  label?: string;
}) {
  return (
    <div className={classes.skeletons} role="status" aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height={height} width={i % 2 ? "72%" : "100%"} />
      ))}
    </div>
  );
}

export function EmptyLine({ children }: { children: ReactNode }) {
  return <div className={classes.empty}>{children}</div>;
}

/* ------------------------------------------------------------------ */
/* ComingSoon / NeedsRole                                              */
/* ------------------------------------------------------------------ */

export function ComingSoon({
  icon,
  title,
  description,
  onNotify,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  onNotify?: () => void;
}) {
  return (
    <div className={cx(classes.surface, classes.centerPanel)}>
      <span className={classes.panelIcon}>{icon}</span>
      <Tag tone="info" style={{ height: 20, fontSize: 11 }}>
        Coming soon
      </Tag>
      <h1>{title}</h1>
      <p>{description}</p>
      {onNotify && (
        <UnstyledButton
          onClick={onNotify}
          style={{
            marginTop: 8,
            height: 32,
            padding: "0 14px",
            border: "1px solid var(--na-border)",
            font: "500 13px/1 var(--na-font-sans)",
          }}
        >
          Notify me
        </UnstyledButton>
      )}
    </div>
  );
}

export function NeedsRole({
  area,
  role,
  yourRole,
  action,
}: {
  area: string;
  role: string;
  yourRole: string;
  action?: ReactNode;
}) {
  return (
    <div className={cx(classes.surface, classes.centerPanel)}>
      <IconLock size={28} className={classes.panelIcon} />
      <h1 style={{ fontSize: 22, lineHeight: "28px" }}>
        {area} needs the {role} role
      </h1>
      <p>Your role: {yourRole}</p>
      {action}
    </div>
  );
}
